// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getCommon, getHeader } from "@hardwoods/App/Store/Selectors/CommonState";
import Pagination, { PaginationPresentationProps } from "@hardwoods/MobiusOverrides/Pagination/Pagination";
import { controlsContainerBreakPoints } from "@hardwoods/Overrides/Widgets/ProductList/ProductListControlsContainer";
import { constants } from "@hardwoods/XDL/constants";
import GenerateMediaQuery, { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes> add new fields
const enum fields {
    showPagesNumber = "showPageNumber",
    showResultsPerPage = "showResultsPerPage",
    paginationAlignment = "paginationAlignment",
}

interface FieldsProps {
    fields: {
        [fields.showPagesNumber]: boolean;
        [fields.showResultsPerPage]: boolean;
        [fields.paginationAlignment]: "flex-start" | "center" | "flex-end";
    };
}
// </XNG-Changes>

const mapStateToProps = (state: CustomAppState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
    isMobile: getCommon(state).isMobile,
    headerHeight: getHeader(state).headerHeight,
});

const mapDispatchToProps = {
    addProductFilters,
};

// <XNG-Changes>
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & FieldsProps;
// </XNG-Changes>

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

// XNG-Changes
export const productListControlsContainerBreakpoints = { lg: 1160, md: 1042, sm: 550, xs: 0 };
const CustomMediaQuery = GenerateMediaQuery(productListControlsContainerBreakpoints);

// <XNG-Changes> Update Style
export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 0;
                            `,
                            null,
                            null,
                            css`
                                margin: 0;
                            `,
                            null,
                        ],
                        "min",
                    )};

                margin: 0;
            `,
        },
        selectProps: {
            labelProps: {
                size: 13,
                color: "text.main",
                css: css`
                    width: unset;
                    letter-spacing: 0;
                    line-height: 22px;
                    ${xScreen.lte(controlsContainerBreakPoints.sm)} {
                        min-width: 65px;
                    }
                `,
            },
            cssOverrides: {
                formField: css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                    max-width: 160px;
                    min-width: 160px;
                    ${xScreen.lte(controlsContainerBreakPoints.sm)} {
                        width: 270px;
                        max-width: none;
                    }
                `,
                formInputWrapper: css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")}
                    width: calc(100% - 65px);
                `,
            },
        },
    },
};

const paginationAlignmentStyle = (paginationAlignment: "flex-start" | "center" | "flex-end") => {
    return {
        pagination: {
            cssOverrides: {
                pagination: css`
                    justify-content: ${paginationAlignment ?? "flex-start"};
                `,
            },
        },
    };
};

// </XNG-Changes>

const ProductListPagination = ({
    addProductFilters,
    hidePagination,
    pagination,
    fields,
    isMobile,
    headerHeight,
}: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const prevPagination = usePrevious(pagination);
    const alignStyle = paginationAlignmentStyle(fields.paginationAlignment);
    const [styles, setStyle] = React.useState(() => mergeWithCss(paginationStyles, alignStyle));

    // <XNG-Changes ML-807>
    const handleScroll = () => {
        if (pagination && prevPagination && prevPagination.currentPage !== pagination.currentPage) {
            const element = document.getElementById("RightProductListColumns");
            const box = element?.getBoundingClientRect();
            const scrollTop = window.pageYOffset;
            if (element) {
                let offsetHeaderTop = constants.header.size.sticky + 10;
                if (isMobile) {
                    offsetHeaderTop = headerHeight;
                }
                const offset = (box?.top ?? 0) + scrollTop;
                window.scrollTo(0, offset - offsetHeaderTop);
            }
        }
    };
    // <XNG-Changes>

    // <XNG-Changes ML-807>
    useEffect(() => {
        setTimeout(() => {
            handleScroll();
        }, 0);
    }, [pagination]);
    // <XNG-Changes>

    // <XNG-Changes: ML-952>
    useEffect(() => {
        // To align pagination
        const alignStyle = paginationAlignmentStyle(fields.paginationAlignment);
        const paginationComponentStyles = mergeWithCss(paginationStyles, alignStyle);
        setStyle(paginationComponentStyles);
    }, [fields.paginationAlignment]);
    // </XNG-Changes: ML-952>

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    // <XNG-Changes>
    const showPageNumber = fields.showPageNumber === undefined ? true : fields.showPageNumber;
    const showResultsPerPage = fields.showResultsPerPage === undefined ? true : fields.showResultsPerPage;
    // </XNG-Changes>

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageLabel={`${translate("Per Page")}`}
            resultsPerPageOptions={pagination.pageSizeOptions}
            showPagesNumber={showPageNumber}
            showResultsPerPage={showResultsPerPage}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="ProductList-PageSize"
            showCustomPaginationStyle={true}
            data-test-selector="productList_pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
        />
    );
};

// <XNG-Changes> add fieldDefinitions
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showPagesNumber,
                displayName: "Show Pages Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showResultsPerPage,
                displayName: "Show Result per page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.paginationAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
